import { useState } from "react";
import { Link } from "react-router-dom";

interface Advisor {
  name: string;
  role: string;
  photo: string;
}

interface Service {
  id: number;
  icon: string;
  title: string;
  description: string;
  features: string[];
  image: string;
  advisor: Advisor;
}

interface Props {
  service: Service;
  isActive: boolean;
  onToggle: () => void;
}

export default function ServiceCard({ service, isActive, onToggle }: Props) {
  return (
    <div className="bg-[#F8F7F4] rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-300 flex flex-col">
      {/* Image */}
      <div className="h-52 overflow-hidden relative flex-shrink-0">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="p-7 flex flex-col flex-1">
        {/* Title row */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#6B7F5C]/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className={`${service.icon} text-[#6B7F5C] text-lg`} />
          </div>
          <h2 className="text-lg font-bold text-[#1A1A1A] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {service.title}
          </h2>
        </div>

        <p className="text-[#6B6B6B] text-sm leading-relaxed mb-5">
          {service.description}
        </p>

        {/* Advisor badge */}
        <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 mb-5 border border-gray-100">
          <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
            <img
              src={service.advisor.photo}
              alt={service.advisor.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-[#6B6B6B] leading-none mb-0.5">Asesor responsable</p>
            <p className="text-sm font-semibold text-[#1A1A1A] truncate" translate="no">{service.advisor.name}</p>
            <p className="text-xs text-[#6B7F5C]" translate="no">{service.advisor.role}</p>
          </div>
          <Link
            to="/equipo"
            className="ml-auto flex-shrink-0 w-7 h-7 flex items-center justify-center text-[#6B6B6B] hover:text-[#6B7F5C] transition-colors cursor-pointer"
            title="Ver perfil"
          >
            <i className="ri-arrow-right-up-line text-base" />
          </Link>
        </div>

        {/* Features toggle */}
        <button
          onClick={onToggle}
          className="text-[#6B7F5C] text-sm font-medium flex items-center gap-1 mb-3 cursor-pointer"
        >
          {isActive ? "Ocultar detalles" : "Ver qué incluye"}
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={isActive ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </div>
        </button>

        {isActive && (
          <ul className="space-y-2 mb-4">
            {service.features.map((feat, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-[#6B7F5C]" />
                </div>
                {feat}
              </li>
            ))}
          </ul>
        )}

        {/* CTA */}
        <div className="mt-auto pt-2">
          <Link
            to="/contacto"
            className="w-full flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#6B7F5C] text-white text-sm font-medium py-3 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Solicitar información
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
